import { OCCCustomFormFieldType } from "common/records/CustomFormSchemaFields";
import { jobResultingConfigFromJSON } from "common/records/JobResultingConfig";
import { RootActions, RootState } from "common/reducers";
import {
  ADD_JOB_RESULTING_FIELD_OPTION,
  DELETE_JOB_RESULTING_FIELD,
  JOB_RESULTING_FIELD_DRAG_END,
  RECEIVE_JOB_RESULTING_CONFIG,
  REMOVE_JOB_RESULTING_FIELD_OPTION,
  RESET_JOB_RESULTING_FIELD,
  UPDATE_JOB_RESULTING_FIELD_TYPE,
  UPDATE_JOB_RESULTING_REQUIRED,
  UPDATE_ORG_JOB_RESULTING_CONFIG,
} from "common/reducers/jobResulting.actions";
import { List, Map, Record } from "immutable";

export const JobResultingStateRecord = Record({
  config: jobResultingConfigFromJSON({}),
  lastSavedConfig: jobResultingConfigFromJSON({}),
});

export const initialState = JobResultingStateRecord();
export type JobResultingState = typeof initialState;
const model = "jobResulting";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.getIn([model])) {
    state = state.setIn([model], initialState);
  }
  switch (action.type) {
    case RECEIVE_JOB_RESULTING_CONFIG: {
      return state
        .setIn([model, "config"], jobResultingConfigFromJSON(action.payload.jobResultingConfig))
        .setIn([model, "lastSavedConfig"], jobResultingConfigFromJSON(action.payload.jobResultingConfig));
    }

    case UPDATE_JOB_RESULTING_REQUIRED: {
      const { fieldName, optionIndex, required } = action.payload;
      const reqSchemaPath = [model, "config", "options", optionIndex, "schema", "required"];

      const requiredFields: string[] = state.getIn(reqSchemaPath) || [];
      if (required && !requiredFields.includes(fieldName)) {
        state = state.setIn(reqSchemaPath, [...requiredFields, fieldName]);
      } else if (!required && requiredFields.includes(fieldName)) {
        state = state.setIn(
          reqSchemaPath,
          requiredFields.filter((name) => name !== fieldName),
        );
      }

      return state;
    }

    case JOB_RESULTING_FIELD_DRAG_END: {
      const { optionIndex, oldIndex, newIndex } = action.payload;
      const config = state.getIn([model, "config"]);
      const schemaProps = config.get("options").get(optionIndex)?.getIn(["schema", "properties"]) || List();
      const sorted = schemaProps.delete(oldIndex).insert(newIndex, schemaProps.get(oldIndex)!);

      state = state.setIn([model, "config", "options", optionIndex, "schema", "properties"], sorted);
      return state;
    }

    case DELETE_JOB_RESULTING_FIELD: {
      const { optionIndex, fieldName, fieldIndex } = action.payload;
      const schemaPath = [model, "config", "options", optionIndex, "schema"];
      state = state.removeIn([...schemaPath, "properties", fieldIndex]);
      // Remove field from required list if it exists
      const requiredList = (state.getIn([...schemaPath, "required"]) || List()) as List<string>;
      if (requiredList.includes(fieldName)) {
        state = state.setIn(
          [...schemaPath, "required"],
          requiredList.filter((name) => name !== fieldName),
        );
      }

      return state;
    }

    case UPDATE_JOB_RESULTING_FIELD_TYPE: {
      const { optionIndex, fieldIndex } = action.payload;
      const optionPath = [model, "config", "options", optionIndex];
      const fieldPath = [...optionPath, "schema", "properties", fieldIndex];
      const fieldName = state.getIn(fieldPath.concat("fieldName")) as string;
      const occ_type = state.getIn(fieldPath.concat("occ_type")) as OCCCustomFormFieldType;
      const prevEnum = state.getIn(fieldPath.concat("enum")) as List<string> | undefined;
      const prevItemsEnum = state.getIn(fieldPath.concat("items", "enum")) as List<string> | undefined;

      // Clean up the field from previous type
      state = state.removeIn([...fieldPath, "format"]);
      state = state.removeIn([...fieldPath, "type"]);
      state = state.removeIn([...fieldPath, "multipleOf"]);
      state = state.removeIn([...fieldPath, "uniqueItems"]);
      state = state.removeIn([...fieldPath, "enum"]);
      // A reloaded non-checkbox field is a Record whose `items` key holds `undefined`.
      state = state.removeIn([...fieldPath, "items"]);
      state = state.removeIn([...optionPath, "uiSchema", fieldName, "ui:widget"]);

      switch (occ_type) {
        case "text":
          state = state.setIn([...fieldPath, "type"], "string");
          break;
        case "multi-line-text":
          state = state.setIn([...fieldPath, "type"], "string");
          state = state.setIn([...optionPath, "uiSchema", fieldName, "ui:widget"], "textarea");
          break;
        case "date-time":
          state = state.setIn([...fieldPath, "type"], "string");
          state = state.setIn([...fieldPath, "format"], "date-time");
          break;
        case "date":
          state = state.setIn([...fieldPath, "type"], "string");
          state = state.setIn([...fieldPath, "format"], "date");
          break;
        case "time":
          state = state.setIn([...fieldPath, "type"], "string");
          state = state.setIn([...fieldPath, "format"], "time");
          break;
        case "integer":
          state = state.setIn([...fieldPath, "type"], "integer");
          break;
        case "currency":
          state = state.setIn([...fieldPath, "type"], "number");
          state = state.setIn([...fieldPath, "multipleOf"], 0.01);
          break;
        case "email":
          state = state.setIn([...fieldPath, "type"], "string");
          state = state.setIn([...fieldPath, "format"], "email");
          break;
        case "single-select":
          state = state.setIn([...fieldPath, "enum"], prevEnum ?? prevItemsEnum);
          break;
        case "checkbox-multi-select":
          state = state.setIn([...fieldPath, "type"], "array");
          state = state.setIn([...fieldPath, "uniqueItems"], true);
          state = state.setIn([...fieldPath, "items"], Map({ enum: prevItemsEnum ?? prevEnum ?? List() }));
          state = state.setIn([...optionPath, "uiSchema", fieldName, "ui:widget"], "CheckboxesWidget");
          break;
        default:
          break;
      }
      return state;
    }

    case ADD_JOB_RESULTING_FIELD_OPTION: {
      const { optionIndex, fieldIndex, isMultiCheck } = action.payload;
      const fieldPath = [model, "config", "options", optionIndex, "schema", "properties", fieldIndex];
      const enumPath = isMultiCheck ? [...fieldPath, "items", "enum"] : [...fieldPath, "enum"];

      return state.updateIn(enumPath, (value) => {
        const newValue = (value || List()).push("");
        return newValue;
      });
    }

    case REMOVE_JOB_RESULTING_FIELD_OPTION: {
      const { optionIndex, fieldIndex, fieldOptionIndex, isMultiCheck } = action.payload;
      const fieldPath = [model, "config", "options", optionIndex, "schema", "properties", fieldIndex];
      const enumPath = isMultiCheck ? [...fieldPath, "items", "enum"] : [...fieldPath, "enum"];

      return state.removeIn([...enumPath, fieldOptionIndex]);
    }

    case RESET_JOB_RESULTING_FIELD: {
      const { optionIndex, fieldIndex, lastEditedField } = action.payload;
      if (!lastEditedField)
        return state.removeIn([model, "config", "options", optionIndex, "schema", "properties", fieldIndex]);

      return state.setIn(
        [model, "config", "options", optionIndex, "schema", "properties", fieldIndex],
        lastEditedField,
      );
    }

    case UPDATE_ORG_JOB_RESULTING_CONFIG: {
      const { orgId } = action.payload;

      return state.setIn(
        ["orgs", "byId", orgId, "preferences", "config", "job_resulting"],
        state.getIn([model, "config"]),
      );
    }

    default:
      return state;
  }
};
